'use client';

import * as React from 'react';
import { Clock } from 'lucide-react';
import { cn } from '@/utils/shad';
import { Button } from '@/comps/primitives/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/comps/primitives/popover';

interface TimePickerProps {
  value: string;
  onChange: (time: string) => void;
  label: string;
  className?: string;
}

export function TimePicker({ value, onChange, label, className }: TimePickerProps) {
  const [open, setOpen] = React.useState(false);

  const handleTimeSelect = (hours: number, minutes: number) => {
    const timeString = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    onChange(timeString);
    setOpen(false);
  };

  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        times.push({ hour, minute });
      }
    }
    return times;
  };

  const timeOptions = generateTimeOptions();

  return (
    <div className={cn(label ? 'space-y-2' : '', className)}>
      {label && <label className="text-xs text-muted-foreground">{label}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="h-8 w-auto min-w-20 justify-start px-2 text-left font-normal"
            size="sm"
          >
            <Clock className="mr-1.5 h-3 w-3" />
            {value || 'Select time'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <div className="max-h-60 overflow-y-auto">
            <div className="grid grid-cols-1 gap-1 p-2">
              {timeOptions.map(({ hour, minute }) => {
                const timeString = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                const isSelected = value === timeString;

                return (
                  <button
                    key={timeString}
                    onClick={() => handleTimeSelect(hour, minute)}
                    className={cn(
                      'rounded px-3 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground',
                      isSelected && 'bg-accent font-medium text-accent-foreground',
                    )}
                  >
                    {timeString}
                  </button>
                );
              })}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
